import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import type {
  AdminCatalogQuery,
  CatalogCollection,
  CharacterCategory,
  CharacterType,
} from '../gql/graphql';
import {
  AdminCatalogDocument,
  AdminDeleteUploadDocument,
  AdminUpsertCatalogItemDocument,
} from '../graphql/operations';
import {
  CHARACTER_CATEGORIES,
  CHARACTER_TYPES,
  hasArtwork,
  hasCategory,
  hasCharacterType,
  hasDescription,
  hasPromptDescription,
  useLabels,
} from '../labels';
import { ArtworkField, type ArtworkUpload } from './ArtworkField';

export type CatalogItem = AdminCatalogQuery['adminCatalog'][number];

// The API's own rules, so a typo costs no round trip -- and, through the same
// dictionaries the API's keys point at, wording that cannot disagree with it in
// front of an operator.
export const ID_MESSAGE_KEY = 'catalog.form.errors.id';
export const LABEL_MESSAGE_KEY = 'catalog.form.errors.labelRequired';
export const LABEL_LENGTH_MESSAGE_KEY = 'catalog.form.errors.labelLength';
export const CATEGORY_MESSAGE_KEY = 'catalog.form.errors.category';
export const CHARACTER_TYPE_MESSAGE_KEY = 'catalog.form.errors.characterType';
export const DESCRIPTION_LENGTH_MESSAGE_KEY = 'catalog.form.errors.descriptionLength';
export const PROMPT_DESCRIPTION_LENGTH_MESSAGE_KEY = 'catalog.form.errors.promptDescriptionLength';
const CLEANUP_MESSAGE_KEY = 'catalog.form.artworkErrors.cleanupFailed';
const ID_PATTERN = /^[a-z0-9-]{1,50}$/;
const LABEL_MAX = 100;
const DESCRIPTION_MAX = 500;
const PROMPT_DESCRIPTION_MAX = 2000;

/**
 * `labels` is an object rather than two flat fields on purpose: the API reports
 * a failed rule under its dotted path (`labels.ru`), and Mantine reads that
 * same path, so `form.setErrors(failure.fields)` lands the server's message
 * under the right input with no mapping in between.
 */
type FormValues = {
  id: string;
  labels: { ru: string; en: string };
  category: CharacterCategory | null;
  characterType: CharacterType | null;
  artworkUrl: string;
  description: string;
  promptDescription: string;
  sortOrder: number | string;
};

/**
 * One form for creating and for editing, because the API has one operation for
 * both: `adminUpsertCatalogItem` keys on the id, which is a slug the operator
 * chooses. That is also why the id is read-only when editing -- changing it
 * would create a second row rather than rename this one.
 *
 * A row is edited in every language at once: Russian is what the app falls back
 * to and is required, English is optional and an empty field clears it.
 */
export function CatalogItemModal({
  collection,
  item,
  opened,
  onClose,
}: {
  collection: CatalogCollection;
  item: CatalogItem | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const labels = useLabels();
  const editing = item !== null;
  const [upsert, { loading, error }] = useMutation(AdminUpsertCatalogItemDocument, {
    // The response is one row; whether the list gained a member is something
    // only the list itself can answer.
    refetchQueries: [{ query: AdminCatalogDocument, variables: { collection } }],
    awaitRefetchQueries: true,
  });
  const [deleteUpload] = useMutation(AdminDeleteUploadDocument);
  /**
   * Every file uploaded while this modal has been open. An upload lands in the
   * object store immediately, but only saving the row makes anything point at
   * it — so whatever the row does not end up wearing is thrown away when the
   * modal goes, including the picture that a second upload replaced.
   */
  const uploads = useRef<ArtworkUpload[]>([]);

  const form = useForm<FormValues>({
    // Controlled, so the artwork preview follows what is being typed.
    mode: 'controlled',
    initialValues: {
      id: item?.id ?? '',
      labels: { ru: item?.labels.ru ?? '', en: item?.labels.en ?? '' },
      category: item?.category ?? null,
      characterType: item?.characterType ?? null,
      artworkUrl: item?.artworkUrl ?? '',
      description: item?.description ?? '',
      promptDescription: item?.promptDescription ?? '',
      sortOrder: item?.sortOrder ?? '',
    },
    validate: {
      id: (value) => (ID_PATTERN.test(value.trim()) ? null : t(ID_MESSAGE_KEY)),
      labels: {
        ru: (value) => {
          const trimmed = value.trim();

          if (!trimmed) return t(LABEL_MESSAGE_KEY);

          return trimmed.length > LABEL_MAX ? t(LABEL_LENGTH_MESSAGE_KEY) : null;
        },
        // Optional: only its length is a rule, because "no translation yet" is
        // a normal state for a row.
        en: (value) => (value.trim().length > LABEL_MAX ? t(LABEL_LENGTH_MESSAGE_KEY) : null),
      },
      category: (value) => (hasCategory(collection) && !value ? t(CATEGORY_MESSAGE_KEY) : null),
      characterType: (value) =>
        hasCharacterType(collection) && !value ? t(CHARACTER_TYPE_MESSAGE_KEY) : null,
      description: (value) =>
        hasDescription(collection) && value.trim().length > DESCRIPTION_MAX
          ? t(DESCRIPTION_LENGTH_MESSAGE_KEY)
          : null,
      promptDescription: (value) =>
        hasPromptDescription(collection) && value.trim().length > PROMPT_DESCRIPTION_MAX
          ? t(PROMPT_DESCRIPTION_LENGTH_MESSAGE_KEY)
          : null,
    },
  });

  /**
   * Drop what the row is not going to keep. Best-effort by design: the object
   * is already in the store, the operator is on their way out, and an
   * unreferenced file is a cheaper failure than a modal that refuses to close.
   */
  function discardUploads(keptUrl: string | null) {
    const stale = uploads.current.filter((upload) => upload.publicUrl !== keptUrl);

    uploads.current = [];

    for (const upload of stale) {
      void deleteUpload({ variables: { key: upload.key } }).catch(() => {
        notifications.show({ color: 'yellow', message: t(CLEANUP_MESSAGE_KEY) });
      });
    }
  }

  function cancel() {
    discardUploads(null);
    onClose();
  }

  async function save(values: FormValues) {
    const sortOrder = values.sortOrder === '' ? null : Number(values.sortOrder);
    const en = values.labels.en.trim();
    const artworkUrl = values.artworkUrl.trim();
    const description = values.description.trim();
    const promptDescription = values.promptDescription.trim();

    try {
      await upsert({
        variables: {
          collection,
          input: {
            id: values.id.trim(),
            // `null` rather than `''`: the API stores "no translation" as no
            // translation at all, and an empty string is how a form says so.
            labels: { ru: values.labels.ru.trim(), en: en || null },
            category: hasCategory(collection) ? values.category : null,
            characterType: hasCharacterType(collection) ? values.characterType : null,
            artworkUrl: hasArtwork(collection) ? artworkUrl : null,
            description: hasDescription(collection) ? description || null : null,
            promptDescription: hasPromptDescription(collection) ? promptDescription || null : null,
            sortOrder: sortOrder !== null && Number.isFinite(sortOrder) ? sortOrder : null,
          },
        },
      });

      notifications.show({
        color: 'teal',
        message: editing ? t('common.saved') : t('catalog.form.added'),
      });
      // The row now points at one of the uploads (or at none); the rest are
      // nobody's. The picture this replaced, if it was ours, is the server's to
      // delete -- it knows which URLs it wrote.
      discardUploads(artworkUrl || null);
      onClose();
    } catch (thrown) {
      const failure = toApiError(thrown as ErrorLike);

      if (failure && Object.keys(failure.fields).length > 0) form.setErrors(failure.fields);
    }
  }

  const apiError = toApiError(error);

  return (
    <Modal
      opened={opened}
      onClose={cancel}
      title={editing ? t('catalog.form.editTitle') : t('catalog.form.newTitle')}
      centered
    >
      {/* Bound here rather than in the body: `form.onSubmit` takes a callback
          that reads the pending uploads, and building it during render is what
          the rules of hooks (rightly) call reading a ref too early. */}
      <form onSubmit={(event) => void form.onSubmit(save)(event)} noValidate>
        <Stack gap="md">
          {/* A headline on its own only when there is no field to hang it on. */}
          {apiError && Object.keys(apiError.fields).length === 0 && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {apiError.message}
            </Alert>
          )}

          <TextInput
            label={t('catalog.form.id')}
            description={t('catalog.form.idDescription')}
            placeholder={t('catalog.form.idPlaceholder')}
            disabled={editing}
            {...form.getInputProps('id')}
          />

          <TextInput
            label={t('catalog.nameRu')}
            placeholder={t('catalog.form.nameRuPlaceholder')}
            {...form.getInputProps('labels.ru')}
          />

          <TextInput
            label={t('catalog.nameEn')}
            description={t('catalog.form.nameEnDescription')}
            placeholder={t('catalog.form.nameEnPlaceholder')}
            {...form.getInputProps('labels.en')}
          />

          {hasCategory(collection) && (
            <Select
              label={t('catalog.form.category')}
              placeholder={t('catalog.form.categoryPlaceholder')}
              // Inside the modal rather than in a portal on `body`: the modal
              // marks everything outside itself `aria-hidden`, which would
              // otherwise hide this dropdown from assistive technology (and
              // from anything else reading the accessibility tree).
              comboboxProps={{ withinPortal: false }}
              data={CHARACTER_CATEGORIES.map((value) => ({
                value,
                label: labels.characterCategory(value),
              }))}
              {...form.getInputProps('category')}
            />
          )}

          {hasCharacterType(collection) && (
            <Select
              label={t('catalog.form.characterType')}
              placeholder={t('catalog.form.characterTypePlaceholder')}
              comboboxProps={{ withinPortal: false }}
              data={CHARACTER_TYPES.map((value) => ({
                value,
                label: labels.characterType(value),
              }))}
              {...form.getInputProps('characterType')}
            />
          )}

          {hasArtwork(collection) && (
            <ArtworkField
              collection={collection}
              itemId={form.values.id.trim()}
              // The object key is built from the id, so a new row has to be
              // named before it can have a picture.
              idReady={ID_PATTERN.test(form.values.id.trim())}
              value={form.values.artworkUrl}
              onChange={(artworkUrl) => {
                form.setFieldValue('artworkUrl', artworkUrl);
              }}
              onUploaded={(upload) => {
                uploads.current = [...uploads.current, upload];
              }}
              onFieldErrors={(fields) => {
                form.setErrors(fields);
              }}
            />
          )}

          {hasDescription(collection) && (
            <Textarea
              label={t('catalog.form.description')}
              description={t('catalog.form.descriptionDescription')}
              autosize
              minRows={2}
              maxRows={6}
              {...form.getInputProps('description')}
            />
          )}

          {hasPromptDescription(collection) && (
            <Textarea
              label={t('catalog.form.promptDescription')}
              description={t('catalog.form.promptDescriptionDescription')}
              autosize
              minRows={2}
              maxRows={10}
              {...form.getInputProps('promptDescription')}
            />
          )}

          <NumberInput
            label={t('catalog.form.sortOrder')}
            description={t('catalog.form.sortOrderDescription')}
            min={0}
            allowDecimal={false}
            {...form.getInputProps('sortOrder')}
          />

          <Group justify="end">
            <Button variant="default" onClick={cancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={loading}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
